import React, { useContext, useMemo, useState } from "react";
import { Modal, Form, Input, Select, message, ColorPicker } from "antd";
import { State } from "country-state-city";
import { BranchOperation } from "../../../../../serviceoperation/BranchOperation";
import { TimeZoneOperation } from "../../../../../serviceoperation/TimezoneOperation";
import { TimeZoneType } from "../../../../schedule/modal/timeZone-types";
import { CompanyContext } from "../../../../../contexts/company-context";

interface LocationModalProps {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

const LocationModal: React.FC<LocationModalProps> = ({
  open,
  onClose,
  onSuccess,
}) => {
  const [form] = Form.useForm();
  const branchOps = useMemo(() => new BranchOperation(), []);
  const timeZoneOps = new TimeZoneOperation();
  const [timeZones, setTimeZone] = useState<TimeZoneType[]>([]);
  const [confirmLoading, setConfirmLoading] = useState<boolean>(false);
  const { timeZone } = Intl.DateTimeFormat().resolvedOptions();
  const { selectedCompany } = useContext(CompanyContext);
  useMemo(() => {
    timeZoneOps.getTimeZoneAsync().then((value) => {
      setTimeZone(
        value.map((s) => ({
          key: s.id,
          name: s.id,
        })),
      );
    });
  }, []);

  const handleSubmit = async () => {
    try {
      setConfirmLoading(true);
      const values = await form.validateFields();
      branchOps
        .postBranchAsync(selectedCompany?.corporationId as number, {
          branchName: values.name,
          branchCode: "",
          branchDescription: values.description,
          addressLine1: values.address,
          city: values.city,
          state: values.state,
          zip: values.zipCode,
          country: "US",
          timeZoneId: values.timeZoneId,
          color: values.color?.toHexString?.() || "#1677ff",
        })
        .then((x) => {
          if (x.statusCode === 201) {
            message.success("Successfully created a location!");
            form.resetFields();
            setConfirmLoading(false);
            onSuccess();
          } else {
            if (x.statusCode == 402) {
              message.error(x.statusMessage);
            } else {
              message.error(
                "An error has occured while processing your request!",
              );
            }
            setConfirmLoading(false);
          }
        });
    } catch (error) {
      setConfirmLoading(false);
      message.error("An error has occured while processing your request!");
    }
  };

  return (
    <Modal
      title="Add New Location"
      open={open}
      onOk={handleSubmit}
      onCancel={onClose}
      okText="Create Location"
      confirmLoading={confirmLoading}
    >
      <Form form={form} layout="vertical" className="mt-4">
        <Form.Item
          name="name"
          label="Location Name"
          rules={[{ required: true, message: "Please enter location name" }]}
        >
          <Input placeholder="e.g., Downtown Office" />
        </Form.Item>

        <Form.Item
          name="description"
          label="Location Description"
          rules={[
            { required: true, message: "Please enter location description" },
          ]}
        >
          <Input placeholder="e.g., Downtown Office" />
        </Form.Item>

        <Form.Item
          name="timeZoneId"
          rules={[
            {
              required: true,
              message: "Please select a timezone!",
            },
          ]}
          initialValue={timeZone}
        >
          <Select
            placeholder="Select TimeZone"
            defaultValue={timeZone}
            allowClear
            showSearch
            optionFilterProp="children"
            filterOption={(input, option) =>
              (option?.label ?? "").toLowerCase().includes(input.toLowerCase())
            }
            options={timeZones.map((tmz) => {
              return {
                value: tmz.key,
                label: tmz.name,
              };
            })}
          />
        </Form.Item>

        <Form.Item
          name="address"
          label="Street Address"
          rules={[{ required: true, message: "Please enter street address" }]}
        >
          <Input placeholder="e.g., 123 Business Park Drive" />
        </Form.Item>

        <div
          style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}
        >
          <Form.Item
            name="city"
            label="City"
            rules={[{ required: true, message: "Please enter city" }]}
          >
            <Input placeholder="e.g., San Francisco" />
          </Form.Item>

          <Form.Item
            name="state"
            label="State"
            rules={[{ required: true, message: "Please select state" }]}
          >
            <Select
              showSearch
              placeholder="Select State"
              allowClear
              optionFilterProp="children"
              filterOption={(input, option) =>
                (option?.label ?? "")
                  .toLowerCase()
                  .includes(input.toLowerCase())
              }
              options={State.getStatesOfCountry("US").map((state) => {
                return {
                  value: state.isoCode,
                  label: state.name,
                };
              })}
            />
          </Form.Item>
        </div>

        <Form.Item
          name="zipCode"
          label="ZIP Code"
          rules={[
            { required: true, message: "Please enter ZIP code" },
            {
              pattern: /^\d{5}(-\d{4})?$/,
              message: "Please enter a valid ZIP code",
            },
          ]}
        >
          <Input placeholder="e.g., 94105" />
        </Form.Item>
        <Form.Item name="color" label="Color Coordinate this location">
          <ColorPicker />
        </Form.Item>
      </Form>
    </Modal>
  );
};

export default LocationModal;
